/* eslint-disable import/no-unresolved */
import { ParsedUrlQuery } from 'querystring';

import { useEffect, useMemo, useState } from 'react';

import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Navigation } from 'swiper';
import { useSearchParams } from 'next/navigation';

import { HotelEventsViewModel } from '@api/v3';
import Typography from '@components/Typography';
import { ArrowLeft, RightArrowIcon } from '@assets/icons';
import CustomIcon from '@components/utils/CustomIcon';
import { paramsToObject } from '@components/views/utils';

import {
  Container,
  CustomSwipeButton,
  SingleItemWrapper,
  SwiperWrapper,
} from './styled';
// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import EventItem from './EventItem';
import EventDetailModal from '../EventDetailModal';

type Props = {
  events: HotelEventsViewModel[];
};

const DesktopEvents = ({ events }: Props) => {
  const [visibleDetailModal, toggleVisibleDetailModal] = useState(false);
  const [activeEvent, setActiveEvent] = useState<HotelEventsViewModel | null>(
    null
  );
  const [list, setList] = useState<HotelEventsViewModel[]>([]);
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);

  const { in: startDate } = query;

  useEffect(() => {
    if (!startDate) setList(events);
    else {
      const filtered = events.filter((event) =>
        event.dates?.some(
          (date) =>
            date &&
            new Date(date).getTime() >= new Date(startDate as string).getTime()
        )
      );
      setList(filtered);
    }
  }, [events, startDate]);
  return list.length > 0 ? (
    <Container>
      {visibleDetailModal ? (
        <EventDetailModal
          image={activeEvent?.image ?? ''}
          title={activeEvent?.title ?? ''}
          shortDescription={activeEvent?.shortDescription ?? ''}
          dates={activeEvent?.dates ?? []}
          description={activeEvent?.description ?? ''}
          toggleDetailModal={() => toggleVisibleDetailModal(false)}
        />
      ) : null}
      <Typography variant='Title4' color='D400' weight='Bold'>
        Etkinlikler
      </Typography>

      {list?.length === 1 ? (
        <SingleItemWrapper>
          <EventItem
            {...list[0]}
            isSingleEvent
            toggleDetailModal={() => {
              setActiveEvent(list[0]);
              toggleVisibleDetailModal(true);
            }}
          />
        </SingleItemWrapper>
      ) : (
        <SwiperWrapper>
          {list?.length > 2 && (
            <>
              <CustomSwipeButton className='swiper-button-next'>
                <CustomIcon
                  icon={RightArrowIcon}
                  width={24}
                  paletteColor='NL0'
                />
              </CustomSwipeButton>

              <CustomSwipeButton left className='swiper-button-prev'>
                <CustomIcon
                  icon={ArrowLeft}
                  width={24}
                  height={14}
                  paletteColor='NL0'
                />
              </CustomSwipeButton>
            </>
          )}
          <Swiper
            slidesPerView={2}
            spaceBetween={16}
            loop={false}
            navigation={{
              nextEl: '.swiper-button-next',
              prevEl: '.swiper-button-prev',
            }}
            modules={[Pagination, Navigation]}
          >
            {list?.map((event) => (
              <SwiperSlide key={event.id}>
                <EventItem
                  {...event}
                  toggleDetailModal={() => {
                    setActiveEvent(event);
                    toggleVisibleDetailModal(true);
                  }}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </SwiperWrapper>
      )}
    </Container>
  ) : null;
};

export default DesktopEvents;
